import React, { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../lib/i18n";
import { ymGoal } from "../components/Analytics";

const BookingContext = createContext();
export const useBooking = () => useContext(BookingContext);

export const BookingProvider = ({ children }) => {
  const [exc, setExc] = useState(null);
  const [open, setOpen] = useState(false);
  const openBooking = (excursion) => {
    setExc(excursion || null);
    setOpen(true);
  };
  return (
    <BookingContext.Provider value={{ openBooking }}>
      {children}
      <AnimatePresence>{open && <BookingModal exc={exc} onClose={() => setOpen(false)} />}</AnimatePresence>
    </BookingContext.Provider>
  );
};

const BookingModal = ({ exc, onClose }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", telegram: "",
    preferred_contact: "phone", requested_date: "", adults: 2, children: 0, comment: "", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(lang === "ru" ? "Подтвердите согласие" : "Please confirm consent"); return; }
    setLoading(true);
    try {
      await api.post("/bookings", {
        excursion_id: exc?.id, excursion_title: exc ? (lang === "ru" ? exc.title_ru : exc.title_en) : null,
        customer_name: form.customer_name, phone: form.phone, email: form.email, telegram: form.telegram,
        preferred_contact: form.preferred_contact, requested_date: form.requested_date,
        adults: Number(form.adults), children: Number(form.children), comment: form.comment,
        source_page: window.location.pathname, language: lang,
        consent: form.consent, consent_version: "2026-08-16",
      });
      toast.success(t("thanks"));
      ymGoal("booking_submit");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-argali-brown/30 py-2.5 text-argali-obsidian placeholder-argali-brown/40 focus:border-argali-bronze outline-none transition-colors";

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: "rgba(17,17,15,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="booking-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto no-scrollbar"
        style={{ background: "#F4F0E7" }}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-argali-brown z-10" data-testid="booking-close"><X size={22} /></button>
        <div className="p-8 md:p-10">
          <p className="eyebrow">{t("cta_request")}</p>
          <h3 className="font-serif text-3xl text-argali-obsidian mt-2 mb-1">
            {exc ? (lang === "ru" ? exc.title_ru : exc.title_en) : (lang === "ru" ? "Запрос экскурсии" : "Excursion enquiry")}
          </h3>
          <div className="hair-line my-5" />
          <form onSubmit={submit} className="space-y-5">
            <input required data-testid="booking-name" className={field} placeholder={t("form_name")} value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
            <input required data-testid="booking-phone" className={field} placeholder={t("form_phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="booking-email" className={field} placeholder={t("form_email")} value={form.email} onChange={(e) => set("email", e.target.value)} />
              <input data-testid="booking-telegram" className={field} placeholder="Telegram / WhatsApp" value={form.telegram} onChange={(e) => set("telegram", e.target.value)} />
            </div>
            <input data-testid="booking-date" className={field} placeholder={t("form_date")} value={form.requested_date} onChange={(e) => set("requested_date", e.target.value)} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-argali-brown/60">{t("form_adults")}</label>
                <input type="number" min="1" data-testid="booking-adults" className={field} value={form.adults} onChange={(e) => set("adults", e.target.value)} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-argali-brown/60">{t("form_children")}</label>
                <input type="number" min="0" data-testid="booking-children" className={field} value={form.children} onChange={(e) => set("children", e.target.value)} />
              </div>
            </div>
            <textarea data-testid="booking-comment" rows={2} className={field} placeholder={t("form_comment")} value={form.comment} onChange={(e) => set("comment", e.target.value)} />
            <label className="flex items-start gap-3 text-xs text-argali-brown/70 cursor-pointer">
              <input type="checkbox" data-testid="booking-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 accent-argali-bronze" />
              <span>
                {lang === "ru" ? "Я даю " : "I give "}
                <a href="/personal-data-consent" target="_blank" rel="noreferrer" className="underline text-argali-bronze" onClick={(e) => e.stopPropagation()}>{lang === "ru" ? "согласие на обработку персональных данных" : "consent to personal data processing"}</a>
                {lang === "ru" ? " и ознакомлен(а) с " : " and have read the "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-argali-bronze" onClick={(e) => e.stopPropagation()}>{lang === "ru" ? "Политикой" : "Policy"}</a>.
              </span>
            </label>
            <button type="submit" disabled={loading} data-testid="booking-submit" className="btn-bronze w-full justify-center">
              {loading ? "…" : t("cta_send")}
            </button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BookingProvider;
